import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
    Button, Dropdown, Form, Space, Table, Tag, Tooltip, Typography, theme
} from "antd";
import {
    CheckCircleOutlined,
    CheckCircleTwoTone,
    DeleteOutlined,
    DownOutlined,
    EditOutlined,
    EyeOutlined,
    FileExcelOutlined,
    FilePdfTwoTone,
    FilterOutlined,
    PauseCircleTwoTone,
    PlusOutlined,
    SearchOutlined,
    StopOutlined,
    SyncOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { InscriptionLight, StatutInscription } from "../../../services/inscription";
import { APPLICATION_DATE_FORMAT, APPLICATION_DATE_TIME_FORMAT } from "../../../utils/FormUtils";
import dayjs from "dayjs";
import { ColumnsType } from "antd/es/table";
import { InscriptionViewProps } from "./types";
import { AdminSearchFilter } from "../../../components/common/AdminSearchFilter";
import { SelectionActionBar } from "../../../components/common/SelectionActionBar";
import { ReinscriptionBadge } from "../../../components/common/ReinscriptionBadge";
import { buildUrlWithParams, DOCUMENT_CONTENT_ENDPOINT } from "../../../services/services";
import {
    getLibelleNiveauScolaire,
    getStatutInscriptionOptions,
} from "../../../components/common/commoninputs";
import { getInscriptionFilterConfig } from "./inscriptionFilters";

const { Text } = Typography;

// ─── Constantes de design ─────────────────────────────────────────────────────

/**
 * Mapping statut -> configuration visuelle (couleur badge Ant, label, icône).
 * Centralisé ici pour éviter la répétition et faciliter l'extension.
 */
const STATUT_CONFIG: Record<
    StatutInscription,
    { color: string; label: string; icon: JSX.Element; tagColor: string }
> = {
    [StatutInscription.VALIDEE]: {
        color: "success",
        label: "Validée",
        icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
        tagColor: "success",
    },
    [StatutInscription.PROVISOIRE]: {
        color: "warning",
        label: "Provisoire",
        icon: <PauseCircleTwoTone twoToneColor="#faad14" />,
        tagColor: "warning",
    },
    [StatutInscription.LISTE_ATTENTE]: {
        color: "error",
        label: "Liste d'attente",
        icon: <WarningOutlined style={{ color: "#ff4d4f" }} />,
        tagColor: "error",
    },
    [StatutInscription.REFUSE]: {
        color: "default",
        label: "Refusée",
        icon: <StopOutlined style={{ color: "#8c8c8c" }} />,
        tagColor: "default",
    },
};

// ─── Sous-composant : Badge de statut ────────────────────────────────────────

/**
 * Affiche le statut sous forme de Tag coloré avec icône.
 * Remplace les tooltips isolés par un élément lisible directement dans la cellule,
 * ce qui améliore la scannabilité de la table sans nécessiter d'interaction.
 */
const StatutBadge: FunctionComponent<{ statut: StatutInscription }> = ({ statut }) => {
    const config = STATUT_CONFIG[statut] ?? STATUT_CONFIG[StatutInscription.REFUSE];
    return (
        <Tag
            icon={config.icon}
            color={config.tagColor}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, borderRadius: 6 }}
        >
            {config.label}
        </Tag>
    );
};

// ─── Sous-composant : Boutons d'action d'une ligne ───────────────────────────

interface RowActionsProps {
    inscription: InscriptionLight;
    application: string;
    onValidate: (id: number) => void;
    onDelete: (id: number) => void;
}

/**
 * Groupe d'actions compact pour chaque ligne.
 * Les actions destructives (Supprimer) sont distinctement positionnées à droite
 * avec un séparateur visuel pour réduire les clics accidentels.
 */
const RowActions: FunctionComponent<RowActionsProps> = ({
    inscription,
    application,
    onValidate,
    onDelete,
}) => {
    const navigate = useNavigate();
    const basePath = application === "COURS_ENFANT" ? "/coursEnfants" : "/coursAdultes";

    return (
        <Space size={4} wrap={false}>
            {/* Consultation */}
            <Tooltip title="Consulter">
                <Button
                    size="small"
                    icon={<EyeOutlined />}
                    style={{ color: "#1890ff", borderColor: "#1890ff" }}
                    onClick={() => navigate(`${basePath}/${inscription.idInscription}?readonly=true`)}
                    {...({ testid: `consulter-inscription-${inscription.idInscription}` } as any)}
                />
            </Tooltip>

            {/* Modification */}
            <Tooltip title="Modifier">
                <Button
                    size="small"
                    icon={<EditOutlined />}
                    style={{ color: "#1890ff", borderColor: "#1890ff" }}
                    onClick={() => navigate(`${basePath}/${inscription.idInscription}?readonly=false`)}
                    {...({ testid: `modifier-inscription-${inscription.idInscription}` } as any)}
                />
            </Tooltip>

            {/* Validation */}
            <Tooltip title={inscription.statut === StatutInscription.VALIDEE ? "Déjà validée" : "Valider"}>
                <Button
                    size="small"
                    icon={<CheckCircleOutlined />}
                    style={{
                        color: inscription.statut === StatutInscription.VALIDEE ? "#d9d9d9" : "#52c41a",
                        borderColor: inscription.statut === StatutInscription.VALIDEE ? "#d9d9d9" : "#52c41a",
                    }}
                    disabled={inscription.statut === StatutInscription.VALIDEE}
                    onClick={() => onValidate(inscription.idInscription)}
                    {...({ testid: `valider-inscription-${inscription.idInscription}` } as any)}
                />
            </Tooltip>

            {/* Suppression */}
            <Tooltip title="Supprimer">
                <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onDelete(inscription.idInscription)}
                    {...({ testid: `supprimer-inscription-${inscription.idInscription}` } as any)}
                />
            </Tooltip>

            {/* PDF */}
            {inscription.documentPending ? (
                <Tooltip title="Document en cours de génération, rafraîchissez les résultats dans quelques instants">
                    <Button
                        size="small"
                        icon={<SyncOutlined spin />}
                        disabled
                        style={{ color: "#1890ff", borderColor: "#1890ff" }}
                        {...({ testid: `document-pending-inscription-${inscription.idInscription}` } as any)}
                    />
                </Tooltip>
            ) : inscription.idDocument ? (
                <Tooltip title="Télécharger PDF">
                    <Button
                        size="small"
                        icon={<FilePdfTwoTone twoToneColor="#1890ff" />}
                        onClick={() => {
                            const url = buildUrlWithParams(DOCUMENT_CONTENT_ENDPOINT, { idDocument: inscription.idDocument });
                            window.open(`${process.env.REACT_APP_BASE_URL_API_V1}${url}`, '_blank');
                        }}
                        style={{ color: "#1890ff", borderColor: "#1890ff" }}
                        {...({ testid: `telecharger-pdf-inscription-${inscription.idInscription}` } as any)}
                    />
                </Tooltip>
            ) : (
                <Tooltip title="Le document n'est pas encore disponible">
                    <Button
                        size="small"
                        icon={<FilePdfTwoTone twoToneColor="#1890ff" />}
                        disabled
                        {...({ testid: `document-indisponible-inscription-${inscription.idInscription}` } as any)}
                    />
                </Tooltip>
            )}
        </Space>
    );
};

// ─── Sous-composant : En-tête des résultats ──────────────────────────────────

interface ResultsHeaderProps {
    totalCount: number;
    uniqueCount: number;
    onExport: () => void;
}

/**
 * Bande d'en-tête au-dessus de la table.
 * Regroupe les méta-informations (compteurs) et l'action d'export dans une zone
 * dédiée, séparée visuellement des actions de ligne. Cela respecte le principe
 * de proximité : ce qui agit sur l'ensemble est séparé de ce qui agit sur une ligne.
 */
const ResultsHeader: FunctionComponent<ResultsHeaderProps> = ({ totalCount, uniqueCount, onExport }) => (
    <div
        style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 16px",
            background: "#fafafa",
            borderRadius: "8px 8px 0 0",
            borderBottom: "1px solid #f0f0f0",
            flexWrap: "wrap",
            gap: 8,
        }}
    >
        {/* Compteurs */}
        <Space size={8}>
            <Text type="secondary" style={{ fontSize: 13 }}>
                <strong style={{ color: "rgba(0,0,0,0.85)" }}>{totalCount}</strong> élève(s) ·{" "}
                <strong style={{ color: "rgba(0,0,0,0.85)" }}>{uniqueCount}</strong> inscription(s)
            </Text>
        </Space>

        {/* Export */}
        <Tooltip title="Exporter tous les résultats dans un fichier Excel">
            <Button
                icon={<FileExcelOutlined />}
                onClick={onExport}
                style={{ color: "#52c41a", borderColor: "#52c41a" }}
            >
                Exporter ({totalCount})
            </Button>
        </Tooltip>
    </div>
);

// ─── Sous-composant : État vide ───────────────────────────────────────────────

/**
 * Placeholder affiché quand aucun résultat ne correspond aux filtres.
 * Remplace le div texte brut par un état vide structuré et invitant à agir.
 */
const EmptyResults: FunctionComponent = () => (
    <div
        style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "48px 24px",
            background: "#fff",
            borderRadius: 12,
            border: "1px dashed #d9d9d9",
            marginTop: 16,
            gap: 8,
        }}
        role="status"
        aria-live="polite"
    >
        <FilterOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />
        <Text style={{ color: "#8c8c8c", fontSize: 15 }}>Aucune inscription ne correspond aux filtres</Text>
        <Text type="secondary" style={{ fontSize: 13 }}>
            Modifiez les critères de recherche ci-dessus pour affiner les résultats.
        </Text>
    </div>
);

// ─── Composant principal ──────────────────────────────────────────────────────

export const InscriptionDesktopView: FunctionComponent<InscriptionViewProps> = ({
    application,
    type,
    dataSource,
    selectedInscriptions,
    setSelectedInscriptions,
    periodesOptions,
    onValidateInscription,
    onValidateInscriptions,
    onDeleteInscription,
    onDeleteInscriptions,
    onSearch,
    onExport,
}) => {
    const [form] = Form.useForm();
    const { token } = theme.useToken();

    useEffect(() => {
        if (periodesOptions && periodesOptions.length > 0) {
            form.setFieldValue("idPeriode", periodesOptions[0].value);
        }
    }, [periodesOptions]);

    // ─── Logique de recherche ────────────────────────────────────────────────

    const buildSearchCriteria = () => {
        const { nom, prenom, telephone, statut, noInscription, idPeriode, reinscription } = form.getFieldsValue();
        let dateInscription = form.getFieldValue("dateInscription");
        if (dateInscription) {
            dateInscription = dayjs(dateInscription).format(APPLICATION_DATE_FORMAT);
        }
        const niveaux = form.getFieldValue("niveau");
        const niveauxInternes = form.getFieldValue("niveauInterne");
        const searchType = application === "COURS_ENFANT" ? "ENFANT" : "ADULTE";
        return {
            nom: nom ?? null,
            prenom: prenom ?? null,
            telephone: telephone ?? null,
            statut: statut ?? null,
            dateInscription: dateInscription ?? null,
            niveaux: niveaux ?? null,
            niveauxInternes: niveauxInternes ?? null,
            noInscription: noInscription ?? null,
            idPeriode: idPeriode ?? null,
            reinscription: reinscription ?? null,
            type: searchType,
        };
    };

    const doSearch = async () => {
        await onSearch(buildSearchCriteria());
    };

    // ─── Filtres statiques + optionnels (ajoutés à la demande) ────────────────

    const { staticFilters, optionalFilters } = getInscriptionFilterConfig(application, periodesOptions);
    const [activeOptionalNames, setActiveOptionalNames] = useState<string[]>([]);

    const activeOptionalFilters = optionalFilters.filter((f) => activeOptionalNames.includes(f.name));
    const inactiveOptionalFilters = optionalFilters.filter((f) => !activeOptionalNames.includes(f.name));

    const addFilter = (name: string) => setActiveOptionalNames((prev) => [...prev, name]);
    const removeFilter = (name: string) => {
        // On vide la valeur pour éviter que le filtre continue de s'appliquer une fois masqué
        form.setFieldValue(name, undefined);
        setActiveOptionalNames((prev) => prev.filter((n) => n !== name));
        doSearch();
    };

    // ─── Métriques de la sélection ────────────────────────────────────────────

    const uniqueInscriptionCount = useMemo(
        () => new Set(dataSource?.map((i) => i.idInscription)).size,
        [dataSource]
    );

    // ─── Définition des colonnes ──────────────────────────────────────────────

    /**
     * Colonnes repensées :
     * - N° inscription : colonne fixe à gauche pour ancre visuelle
     * - Nom + Prénom fusionnés visuellement dans une cellule pour réduire la largeur
     * - Statut : Tag coloré + lisible directement (plus de tooltip obligatoire)
     * - Actions : boutons iconiques `type="text"` pour réduire le bruit visuel
     *   (les boutons `type="primary"` bleus répétés 5x créaient une surcharge cognitive)
     */
    const columns: ColumnsType<InscriptionLight> = [
        {
            title: "N° inscription",
            dataIndex: "noInscription",
            key: "noInscription",
            width: 130,
            render: (value: string) => (
                <Text code style={{ fontSize: 14 }}>
                    {value}
                </Text>
            ),
        },
        {
            title: "Élève",
            key: "eleve",
            width: 250,
            render: (_, record) => (
                <div>
                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, lineHeight: 1.4 }}>
                        <Text strong>{record.nom} {record.prenom}</Text>
                        {record.reinscription && <ReinscriptionBadge />}
                    </div>
                    {record.ville && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.ville}
                        </Text>
                    )}
                </div>
            ),
        },
        ...(application === "COURS_ENFANT"
            ? [
                {
                    title: "Niveau",
                    dataIndex: "niveau",
                    key: "niveau",
                    width: 90,
                    render: (value: string) => (
                        <Tag style={{ borderRadius: 4, fontSize: 12 }}>
                            {getLibelleNiveauScolaire(value as any)}
                        </Tag>
                    ),
                } as ColumnsType<InscriptionLight>[number],
            ]
            : []),
        {
            title: "Niveau interne",
            dataIndex: "niveauInterne",
            key: "niveauInterne",
            width: 110,
            render: (value: string) =>
                value ? (
                    <Tag color="blue" style={{ borderRadius: 4, fontSize: 12 }}>
                        {value}
                    </Tag>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: "Téléphone",
            dataIndex: "mobile",
            key: "telephone",
            width: 130,
            render: (value: string) =>
                value ? (
                    <Text style={{ fontVariantNumeric: "tabular-nums" }}>{value}</Text>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: "Statut",
            dataIndex: "statut",
            key: "statut",
            width: 150,
            filters: getStatutInscriptionOptions().map((o) => ({
                text: o.label as string,
                value: o.value as string,
            })),
            onFilter: (value, record) => record.statut === value,
            render: (value: StatutInscription) => <StatutBadge statut={value} />,
        },
        {
            title: "Date inscription",
            dataIndex: "dateInscription",
            key: "dateInscription",
            width: 130,
            sorter: (a, b) =>
                dayjs(a.dateInscription, APPLICATION_DATE_TIME_FORMAT).unix() -
                dayjs(b.dateInscription, APPLICATION_DATE_TIME_FORMAT).unix(),
            render: (value: string) => (
                <Text style={{ fontVariantNumeric: "tabular-nums", fontSize: 13 }}>
                    {dayjs(value, APPLICATION_DATE_TIME_FORMAT).format(APPLICATION_DATE_FORMAT)}
                </Text>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 180,
            fixed: "right",
            render: (_, inscription: InscriptionLight) => (
                <RowActions
                    inscription={inscription}
                    application={application}
                    onValidate={onValidateInscription}
                    onDelete={onDeleteInscription}
                />
            ),
        },
    ];

    // ─── Sélection multiple (inchangée) ──────────────────────────────────────

    const rowSelection = {
        selectedRowKeys: selectedInscriptions.map((i) => i.id),
        onChange: (_keys: React.Key[], rows: InscriptionLight[]) => {
            setSelectedInscriptions(rows);
        },
    };

    // ─── Rendu ───────────────────────────────────────────────────────────────

    const hasResults = dataSource && dataSource.length > 0;

    return (
        <div style={{ display: "flex", flexDirection: "row", gap: 16, alignItems: "flex-start" }}>
            {/* ── Panneau filtres ── */}
            <div
                style={{
                    background: token.colorBgContainer,
                    borderRadius: token.borderRadiusLG,
                    border: `1px solid ${token.colorBorder}`,
                    overflow: "hidden",
                    boxShadow: token.boxShadowSecondary,
                    width: 400,
                    flexShrink: 0,
                }}
            >
                {/* En-tête panneau filtres */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "12px 16px",
                        background: "#001529",
                        borderBottom: `1px solid ${token.colorBorder}`,
                    }}
                >
                    <SearchOutlined style={{ color: "#fff", fontSize: 14 }} />
                    <Text strong style={{ color: "#fff", fontSize: 14 }}>
                        Filtres de recherche
                    </Text>
                </div>

                {/* Corps filtres */}
                <div style={{ padding: "16px 20px" }}>
                    <Form
                        name="adminInscriptionDesktop"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        autoComplete="off"
                        form={form}
                    >
                        <AdminSearchFilter
                            doSearch={doSearch}
                            inputFilters={[...staticFilters, ...activeOptionalFilters]}
                            removableFilterNames={activeOptionalNames}
                            onRemoveFilter={removeFilter}
                            extraActions={
                                <Dropdown
                                    trigger={["click"]}
                                    disabled={inactiveOptionalFilters.length === 0}
                                    menu={{
                                        items: inactiveOptionalFilters.map((f) => ({ key: f.name, label: f.libelle })),
                                        onClick: ({ key }) => addFilter(key),
                                    }}
                                >
                                    <Button icon={<PlusOutlined />} {...({ testid: "ajouter-filtre" } as any)}>
                                        Ajouter un filtre <DownOutlined />
                                    </Button>
                                </Dropdown>
                            }
                        />
                    </Form>
                </div>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                {/* ── Zone résultats ── */}
                {hasResults ? (
                    <div
                        style={{
                            background: token.colorBgContainer,
                            borderRadius: token.borderRadiusLG,
                            border: `1px solid ${token.colorBorder}`,
                            overflow: "hidden",
                            boxShadow: token.boxShadowSecondary,
                        }}
                    >
                        {/* En-tête résultats avec compteurs + export */}
                        <ResultsHeader
                            totalCount={dataSource.length}
                            uniqueCount={uniqueInscriptionCount}
                            onExport={onExport}
                        />

                        {/* Barre d'actions groupées — apparaît uniquement si sélection active */}
                        {selectedInscriptions.length > 0 && (
                            <div style={{ padding: "8px 16px 0 16px" }}>
                                <SelectionActionBar
                                    selectedCount={selectedInscriptions.length}
                                    itemLabel="inscription"
                                    onValidate={() => onValidateInscriptions(selectedInscriptions)}
                                    onDelete={() => onDeleteInscriptions(selectedInscriptions)}
                                    onCancel={() => setSelectedInscriptions([])}
                                />
                            </div>
                        )}

                        {/* Table */}
                        <div style={{ padding: "0 0 8px 0" }}>
                            <Table
                                rowSelection={{ type: "checkbox", ...rowSelection }}
                                columns={columns}
                                dataSource={dataSource}
                                rowKey={(record) => record.id}
                                scroll={{ x: "max-content" }}
                                size="middle"
                                pagination={{
                                    showSizeChanger: true,
                                    pageSizeOptions: ["10", "20", "50", "100"],
                                    defaultPageSize: 10,
                                    showTotal: (total, range) => (
                                        <Text type="secondary" style={{ fontSize: 13 }}>
                                            {range[0]}–{range[1]} sur{" "}
                                            <strong>{total}</strong> résultat(s)
                                        </Text>
                                    ),
                                }}
                                /**
                                 * Mise en évidence des lignes selon le statut :
                                 * un fond très légèrement teinté renforce la lecture
                                 * sans surcharger visuellement la table.
                                 */
                                rowClassName={(record) => {
                                    if (record.statut === StatutInscription.VALIDEE) return "row-validated";
                                    if (record.statut === StatutInscription.LISTE_ATTENTE) return "row-waiting";
                                    return "";
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <EmptyResults />
                )}
            </div>
        </div>
    );
};
